// 1:1 email from the rep's mailbox (spec 06 §4). One idempotency key per opening, reused on retry.
// The UI never resends on its own: every send is a click, guarded by a ref while in flight.
// GAVION ai-email (spec §4, §6): « ✨ Rédiger avec l'IA » fills Objet and the body (the CRM signature appended once,
// ruling P5/F2); the send carries ai_draft_id and whether the rep changed the text. While the AI call runs, the editor,
// Objet, the template list and Envoyer are locked (F11), so nothing typed meanwhile is silently replaced.
import { useQueryClient } from "@tanstack/react-query";
import { Confirm } from "@/components/admin/confirm";
import { useNotify, useRefresh, useTranslate } from "ra-core";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { LazyRichEmailEditor as RichEmailEditor } from "../../email/editor/LazyRichEmailEditor";
import { sanitizeEmailHtml } from "../../email/editor/sanitize";
import { MS_STATUS_KEY } from "../../settings/integrations/MicrosoftCard";
import { errorMessage } from "../api/errorMessage";
import { useM365Api } from "../api/M365ApiContext";
import { M365ApiError, type ComposeAttachment } from "../api/types";
import type { AiDraftResult } from "../../ai/api/types";
import { AiDraftBar } from "./AiDraftBar";
import { AttachmentsInput } from "./AttachmentsInput";
import {
  applyTemplate,
  excludeRecipients,
  hasAiPlaceholder,
  hasPlaceholder,
  hasTypedText,
  isBlankHtml,
  MAX_ATTACHMENTS,
  MAX_TOTAL_BYTES,
  sameVisibleText,
  totalSize,
  withSignature,
} from "./composeModel";
import { RecipientsInput } from "./RecipientsInput";
import {
  TemplateFileStoreContext,
  templateAttachmentsOf,
} from "../../email/templates/templateFiles"; // GAVION template attachments
import { useComposeData } from "./useComposeData";

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId?: number | null;
  contactId?: number | null;
  dealId?: number | null;
  defaultTo: string[];
  /** GAVION ai-email (spec §6 « Modifier »): open prefilled from a proposed draft; its send is marked edited. */
  aiDraft?: { id: number; subject: string; body: string; to: string | null };
  /** GAVION send-fixes: called once the email is sent (the dialer's follow-up banner then closes). */
  onSent?: () => void;
};
type ErrorState = { text: string; connect: boolean };
const NEEDS_CONNECT = new Set([
  "microsoft_not_connected",
  "microsoft_needs_reauth",
]);
const EMPTY_BODY = "<p></p>";

export const ComposeEmailDrawer = ({
  open,
  onOpenChange,
  companyId = null,
  contactId = null,
  dealId = null,
  defaultTo,
  aiDraft,
  onSent,
}: Props) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const api = useM365Api();
  const keyRef = useRef<string>(crypto.randomUUID());
  const inFlight = useRef(false);
  const wasOpen = useRef(open);
  const [to, setTo] = useState(aiDraft?.to ? [aiDraft.to] : defaultTo);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState(aiDraft?.subject ?? "");
  const [html, setHtml] = useState(aiDraft?.body ?? EMPTY_BODY);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<ComposeAttachment[]>([]);
  // GAVION template attachments: the picked template's files, downloaded while « Envoyer » waits
  const fileStore = useContext(TemplateFileStoreContext);
  const filesSeq = useRef(0);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);
  const [signature, setSignature] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [invalidTo, setInvalidTo] = useState<string[]>([]);
  const [invalidCc, setInvalidCc] = useState<string[]>([]);
  const [pendingTemplate, setPendingTemplate] = useState<string | null>(null);
  // GAVION ai-email: the AI draft in the editor (its id, the text as the AI wrote it, and « Modifier » mode)
  const [aiDraftId, setAiDraftId] = useState<number | null>(
    aiDraft?.id ?? null,
  );
  const [aiBaseline, setAiBaseline] = useState<{
    subject: string;
    html: string;
  } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  // review M2: bumped when the late signature is appended, so the editor shows it even while focused
  const [editorSync, setEditorSync] = useState(0);
  const fromProposed = aiDraft !== undefined;
  // F2: an AI body that still needs the CRM signature (the signature had not loaded yet), and whether it has loaded
  const unsigned = useRef<string | null>(aiDraft?.body ?? null);
  const signatureLoaded = useRef(false);
  // the signature as loaded now (applyAiDraft may run from a closure taken before it loaded)
  const signatureRef = useRef("");
  const htmlRef = useRef(html);
  useEffect(() => {
    htmlRef.current = html;
  }, [html]);
  const { templates, vars, suggestedTo } = useComposeData({
    open,
    companyId,
    contactId,
    dealId,
  });

  // An « À » the rep has edited is never refilled.
  const toTouched = useRef(false);
  // A new opening is a new message: fresh key and fields (when the parent keeps the drawer mounted).
  useEffect(() => {
    if (open && !wasOpen.current) {
      keyRef.current = crypto.randomUUID();
      toTouched.current = false;
      setTo(aiDraft?.to ? [aiDraft.to] : defaultTo);
      setCc([]);
      setSubject(aiDraft?.subject ?? "");
      setHtml(aiDraft?.body ?? EMPTY_BODY);
      setAiDraftId(aiDraft?.id ?? null);
      setAiBaseline(null);
      unsigned.current = aiDraft?.body ?? null;
      signatureLoaded.current = false;
      setTemplateId(null);
      setAttachments([]);
      filesSeq.current += 1; // a download still running belongs to the previous opening
      setLoadingFiles(false);
      setError(null);
      setInvalidTo([]);
      setInvalidCc([]);
      setPendingTemplate(null);
    }
    wasOpen.current = open;
  }, [open, defaultTo, aiDraft]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api
      .getSignature()
      .then((s) => {
        if (!alive) return;
        const safe = sanitizeEmailHtml(s);
        setSignature(safe);
        signatureRef.current = safe;
        signatureLoaded.current = true;
        // GAVION ai-email (F2): an AI body shown before the signature loaded is signed now, once, if still untouched.
        const pending = unsigned.current;
        unsigned.current = null;
        if (
          pending !== null &&
          safe &&
          sameVisibleText(htmlRef.current, pending)
        ) {
          const signed = withSignature(pending, safe);
          setHtml(signed);
          setEditorSync((n) => n + 1);
          setAiBaseline((b) =>
            b && b.html === pending ? { ...b, html: signed } : b,
          );
          return;
        }
        // Only an untouched (blank) body receives the signature; typed text is never replaced.
        setHtml((h) => (isBlankHtml(h) ? withSignature(EMPTY_BODY, safe) : h));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [open, api]);

  // An empty « À » is filled from the contact's (else the company's) email once it loads: a deal, or an AI draft
  // queued before the address was known, then opens addressed.
  useEffect(() => {
    if (!open || toTouched.current || suggestedTo.length === 0) return;
    setTo((cur) => (cur.length === 0 ? suggestedTo : cur));
  }, [open, suggestedTo]);

  // Recipients are unique across To and Cc: an address in To is dropped from Cc.
  const changeTo = (v: string[]) => {
    toTouched.current = true;
    setTo(v);
    setCc((c) => excludeRecipients(c, v));
  };
  const changeCc = (v: string[]) => setCc(excludeRecipients(v, to));
  const handleOpenChange = (o: boolean) => {
    if (!o && inFlight.current) return; // never close while a send is in flight
    onOpenChange(o);
  };
  const chooseTemplate = (id: string) => {
    if (id !== "" && hasTypedText(html, signature)) setPendingTemplate(id);
    else applyTemplateId(id);
  };
  /**
   * GAVION template attachments: the files of the template just picked replace the previous template's (files the
   * rep added stay). Over the 10 MB cap with the rep's files, none is added and the rep is told.
   */
  const loadTemplateFiles = async (tpl: (typeof templates)[number] | null) => {
    const seq = ++filesSeq.current;
    const manual = (a: ComposeAttachment[]) =>
      a.filter((x) => x.fromTemplate === undefined);
    setAttachments(manual);
    const list = fileStore && tpl ? templateAttachmentsOf(tpl.attachments) : [];
    if (!fileStore || !tpl || list.length === 0) {
      setLoadingFiles(false);
      return;
    }
    setLoadingFiles(true);
    try {
      const got: ComposeAttachment[] = await Promise.all(
        list.map(async (f) => ({
          name: f.name,
          contentType: f.contentType,
          size: f.size,
          contentBase64: await fileStore.download(f),
          fromTemplate: Number(tpl.id),
        })),
      );
      if (seq !== filesSeq.current) return;
      const mine = manual(attachmentsRef.current);
      if (
        totalSize(mine) + totalSize(got) > MAX_TOTAL_BYTES ||
        mine.length + got.length > MAX_ATTACHMENTS
      ) {
        notify("gavion.compose.template_files_too_large", { type: "error" });
        return;
      }
      setAttachments((a) => [...manual(a), ...got]);
    } catch {
      if (seq === filesSeq.current)
        notify("gavion.compose.template_files_failed", { type: "error" });
    } finally {
      if (seq === filesSeq.current) setLoadingFiles(false);
    }
  };
  const applyTemplateId = (id: string) => {
    const tpl = templates.find((x) => String(x.id) === id);
    setTemplateId(tpl ? Number(tpl.id) : null);
    void loadTemplateFiles(tpl ?? null);
    if (!tpl) return;
    // review M1: a template replaces the AI text, so the email no longer comes from an on-demand draft. A proposed
    // draft opened with « Modifier » stays linked (sent edited), so its card never offers it again (M4).
    setAiDraftId(aiDraft?.id ?? null);
    setAiBaseline(null);
    unsigned.current = null;
    const r = applyTemplate(tpl, vars);
    setSubject(r.subject);
    setHtml(withSignature(r.html, signature));
  };
  const applyAiDraft = (r: AiDraftResult) => {
    // the model writes no signature: the CRM appends the rep's, exactly once (ruling P5/F2)
    const body = signatureLoaded.current
      ? withSignature(r.body, signatureRef.current)
      : r.body;
    unsigned.current = signatureLoaded.current ? null : body;
    setSubject(r.subject);
    setHtml(body);
    setTemplateId(null);
    // review M4: inside « Modifier » the proposed draft stays the linked one (sent edited); the on-demand row is
    // left pending and expires in a day
    setAiDraftId(aiDraft?.id ?? r.draftId);
    setAiBaseline({ subject: r.subject, html: body });
  };
  // Typed text (anything but the last AI text) is never replaced without confirmation.
  // Compared on visible text: the editor may re-serialise the HTML it was given.
  // review M5: a typed Objet counts too.
  const aiNeedsConfirm = () =>
    (hasTypedText(html, signature) &&
      (aiBaseline === null || !sameVisibleText(html, aiBaseline.html))) ||
    (subject.trim() !== "" &&
      (aiBaseline === null || subject.trim() !== aiBaseline.subject.trim()));

  const fail = (text: string) => setError({ text, connect: false });
  const send = async () => {
    if (inFlight.current) return;
    setError(null);
    if (invalidTo.length > 0 || invalidCc.length > 0)
      return fail(t("gavion.compose.fix_invalid"));
    if (to.length === 0) return fail(t("gavion.compose.required_to"));
    if (!subject.trim()) return fail(t("gavion.compose.required_subject"));
    const body = sanitizeEmailHtml(html);
    if (hasPlaceholder(subject, body))
      return fail(t("gavion.compose.placeholder_left"));
    if (hasAiPlaceholder(subject, body))
      return fail(t("gavion.ai.placeholder_left"));
    inFlight.current = true;
    setSending(true);
    try {
      await api.sendEmail({
        idempotencyKey: keyRef.current,
        to,
        cc: excludeRecipients(cc, to),
        subject: subject.trim(),
        html: body,
        companyId,
        contactId,
        dealId,
        templateId,
        attachments,
        aiDraftId,
        aiDraftOnDemand: aiDraftId !== null && !fromProposed,
        aiDraftEdited:
          fromProposed ||
          (aiBaseline !== null &&
            (subject.trim() !== aiBaseline.subject.trim() ||
              !sameVisibleText(html, aiBaseline.html))),
      });
      notify("gavion.compose.sent", { type: "success" });
      onSent?.();
      onOpenChange(false);
      refresh();
    } catch (e) {
      const code = e instanceof M365ApiError ? e.code : "unknown";
      const connect = NEEDS_CONNECT.has(code);
      if (connect)
        void queryClient.invalidateQueries({ queryKey: MS_STATUS_KEY });
      setError({
        text:
          code === "microsoft_not_connected"
            ? t("gavion.compose.not_connected")
            : code.startsWith("ai_draft_")
              ? t("gavion.ai.errors.not_pending")
              : errorMessage(t, e),
        connect,
      });
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="flex w-full flex-col gap-3 overflow-y-auto p-4 sm:max-w-xl">
        <SheetHeader className="p-0">
          <SheetTitle>{t("gavion.compose.title")}</SheetTitle>
        </SheetHeader>
        <RecipientsInput
          id="compose-to"
          label={t("gavion.compose.to")}
          value={to}
          onChange={changeTo}
          onInvalidChange={setInvalidTo}
        />
        <RecipientsInput
          id="compose-cc"
          label={t("gavion.compose.cc")}
          value={cc}
          onChange={changeCc}
          onInvalidChange={setInvalidCc}
        />
        <div className="flex flex-col gap-1">
          <Label htmlFor="compose-template">
            {t("gavion.compose.template")}
          </Label>
          <select
            id="compose-template"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            value={templateId ?? ""}
            disabled={aiBusy}
            onChange={(e) => chooseTemplate(e.target.value)}
          >
            <option value="">{t("gavion.compose.no_template")}</option>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="compose-subject">{t("gavion.compose.subject")}</Label>
          <Input
            id="compose-subject"
            value={subject}
            maxLength={255}
            disabled={aiBusy}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        {companyId !== null && (
          <AiDraftBar
            companyId={companyId}
            contactId={contactId}
            dealId={dealId}
            needsConfirm={aiNeedsConfirm}
            onDraft={applyAiDraft}
            onBusyChange={setAiBusy}
            disabled={sending}
          />
        )}
        <RichEmailEditor
          value={html}
          onChange={setHtml}
          variables={[]}
          disabled={aiBusy}
          forceSyncKey={editorSync}
          ariaLabel={t("gavion.compose.body")}
        />
        <AttachmentsInput
          value={attachments}
          onChange={setAttachments}
          disabled={sending}
        />
        {loadingFiles && (
          <p role="status" className="text-sm text-muted-foreground">
            {t("gavion.compose.template_files_loading")}
          </p>
        )}
        {error && (
          <div role="alert" className="text-sm text-destructive">
            <p>{error.text}</p>
            {error.connect && (
              <Link
                className="text-accent-foreground underline"
                to="/parametres/integrations"
              >
                {t("gavion.compose.connect_link")}
              </Link>
            )}
          </div>
        )}
        <div className="sticky bottom-0 -mx-4 mt-auto flex justify-end gap-2 border-t bg-background px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            disabled={sending}
            onClick={() => handleOpenChange(false)}
          >
            {t("gavion.compose.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void send()}
            disabled={sending || aiBusy || loadingFiles}
            aria-busy={sending}
          >
            {sending ? t("gavion.compose.sending") : t("gavion.compose.send")}
          </Button>
        </div>
        <Confirm
          isOpen={pendingTemplate !== null}
          title="gavion.compose.replace_title"
          content="gavion.compose.replace_body"
          onClose={() => setPendingTemplate(null)}
          onConfirm={() => {
            if (pendingTemplate !== null) applyTemplateId(pendingTemplate);
            setPendingTemplate(null);
          }}
        />
      </SheetContent>
    </Sheet>
  );
};
